import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Switch } from "../ui/switch";
import { useTranslation } from "../../i18n";
import MarkdownEditor from "../MarkdownEditor";
import CronPicker from "./CronPicker";

/**
 * Dynamic form that renders fields based on the selected trigger type's schema.
 * Fields come from the already-loaded trigger types — no per-instance refetch.
 */
export default function CampaignForm({
  fields,
  config,
  onChange,
  disabled = false,
  triggerType,
  placeholders = [],
}) {
  const { t } = useTranslation();

  if (!fields || fields.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm">
        {t("campaign.selectTrigger")}
      </div>
    );
  }

  const handleChange = (name, value) => {
    onChange({ ...config, [name]: value });
  };

  // Fields skipped — handled inline or deprecated
  const HIDDEN_FIELDS = new Set(["deep_link_path"]);

  return (
    <div className="space-y-4">
      {fields
        .filter((f) => !HIDDEN_FIELDS.has(f.name))
        .map((field) => (
          <div key={field.name}>
            {renderField(
              field,
              config[field.name] ?? field.default,
              handleChange,
              disabled,
              triggerType,
              placeholders,
            )}
          </div>
        ))}
    </div>
  );
}

function renderField(
  field,
  value,
  onChange,
  disabled,
  triggerType,
  placeholders,
) {
  switch (field.name) {
    case "push_title_template":
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder="С днём рождения, {firstname}! 🎂"
          />
        </>
      );

    case "push_body_template":
      return (
        <MarkdownEditor
          label={field.label}
          value={value ?? ""}
          onChange={(v) => onChange(field.name, v)}
          triggerType={triggerType}
          placeholders={placeholders}
        />
      );
  }

  switch (field.fieldType) {
    case "cron":
      return (
        <CronPicker
          value={value ?? ""}
          onChange={(v) => onChange(field.name, v)}
          disabled={disabled}
        />
      );

    case "number":
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            type="number"
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) =>
              onChange(field.name, parseInt(e.target.value, 10) || 0)
            }
          />
        </>
      );
    case "text":
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
        </>
      );
    case "textarea":
      return (
        <>
          <Label>{field.label}</Label>
          <textarea
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
            rows={4}
            className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 font-mono resize-y"
          />
        </>
      );
    case "toggle":
      return (
        <div className="flex items-center gap-2">
          <Switch
            checked={!!value}
            onCheckedChange={(checked) => onChange(field.name, checked)}
          />
          <Label>{field.label}</Label>
        </div>
      );
    case "select":
      return (
        <>
          <Label>{field.label}</Label>
          <Select
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Select>
        </>
      );
    default:
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
        </>
      );
  }
}
